import { useIsMobile } from "@/hooks/use-mobile";
import { useBookings } from "@/hooks/bookings/useBookings";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useMagicLinkBookingAccess } from "@/hooks/useMagicLinkBookingAccess";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Link, useNavigate } from "react-router-dom";
import { useState, useRef, useCallback, useEffect } from "react";
import { BookingDetailsModal } from "@/components/bookings/booking-details";
import BookingCard from "@/components/bookings/BookingCard";
import BookingsFilterTabs from "@/components/bookings/BookingsFilterTabs";
import { BookingsLoading, BookingsError, NoBookings, LoadingMore, EndOfResults } from "@/components/bookings/BookingsLoadingStates";
import { useTheme } from "@/hooks/use-theme";
import { usePaymentStatus } from "@/hooks/usePaymentStatus";
import { BookingDetails } from "@/hooks/bookings/types";
const Bookings = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const {
    primaryColor
  } = useTheme();
  const {
    roles
  } = useUserRoles();
  const magicLinkAccess = useMagicLinkBookingAccess();

  // State variables
  const [selectedBooking, setSelectedBooking] = useState<BookingDetails | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState<string | null>(null);
  console.log('[BOOKINGS-PAGE] Component rendered:', {
    isMobile,
    primaryColor,
    timestamp: new Date().toISOString()
  });
  const {
    bookings,
    isLoading,
    isFetchingMore,
    error,
    hasMore,
    fetchMoreBookings,
    filterBookings,
    activeFilter,
    cancelBooking,
    // isAdmin,
    refreshBooking
  } = useBookings();

  // Add payment status handling
  const {
    verifyPayment,
    isProcessing
  } = usePaymentStatus();
  console.log('[BOOKINGS-PAGE] Bookings data:', {
    bookingsCount: bookings.length,
    isLoading,
    error: error?.message || null,
    activeFilter
    // isAdmin
  });

  // Handle URL hash - redirect to new route format for backward compatibility
  useEffect(() => {
    const hash = window.location.hash.slice(1); // Remove the # character
    if (hash) {
      console.log('[BOOKINGS-PAGE] Detected hash-based URL, redirecting to new route:', hash);
      navigate(`/bookings/${hash}`, {
        replace: true
      });
    }
  }, [navigate]);

  // Update selected booking when bookings array changes (from realtime updates)
  useEffect(() => {
    if (selectedBooking && isDetailsModalOpen) {
      const updatedBooking = bookings.find(b => b.id === selectedBooking.id);
      if (updatedBooking && updatedBooking.status !== selectedBooking.status) {
        console.log('[BOOKINGS-PAGE] Updating selected booking from realtime:', {
          bookingId: selectedBooking.id,
          oldStatus: selectedBooking.status,
          newStatus: updatedBooking.status
        });
        setSelectedBooking(updatedBooking);
      }
    }
  }, [bookings, selectedBooking, isDetailsModalOpen]);

  // Observer for infinite scrolling
  const observer = useRef<IntersectionObserver | null>(null);

  // Stable callback function for the intersection observer
  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    if (entries[0]?.isIntersecting && hasMore && !isFetchingMore) {
      console.log("Intersection detected, fetching more bookings");
      fetchMoreBookings();
    }
  }, [fetchMoreBookings, hasMore, isFetchingMore]);

  // Effect to recreate observer with stable dependencies
  useEffect(() => {
    // Clean up previous observer
    if (observer.current) {
      observer.current.disconnect();
    }

    // Don't create observer if loading, fetching more, or no more data
    if (isLoading || !hasMore) return;

    // Create new observer with the stable callback
    observer.current = new IntersectionObserver(handleIntersection, {
      rootMargin: '100px',
      threshold: 0.1
    });

    // Find and observe the sentinel element
    const sentinelElement = document.getElementById('booking-list-sentinel');
    if (sentinelElement) {
      observer.current.observe(sentinelElement);
    }
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [handleIntersection, isLoading, hasMore]);

  // Ref callback for the last element
  const lastBookingElementRef = useCallback((node: HTMLDivElement | null) => {
    // Do nothing here - we'll handle observation in the useEffect
  }, []);
  const handleOpenDetails = (booking: BookingDetails) => {
    console.log('[BOOKINGS-PAGE] Opening details for booking:', booking.id);
    setSelectedBooking(booking);
    setIsDetailsModalOpen(true);
  };
  const handleCloseDetails = () => {
    setIsDetailsModalOpen(false);
    setSelectedBooking(null);
  };
  const handleCancelBooking = async () => {
    if (!bookingToCancel) return;
    console.log('[BOOKINGS-PAGE] Cancelling booking:', bookingToCancel);
    const success = await cancelBooking(bookingToCancel);
    if (success) {
      setBookingToCancel(null);
    }
  };
  const handleBookingUpdate = async (bookingId: string) => {
    console.log('[BOOKINGS-PAGE] Booking update requested, refreshing booking:', bookingId);
    const updatedBooking = await refreshBooking(bookingId);

    // Update the selected booking if it matches and we got fresh data
    if (updatedBooking && selectedBooking?.id === bookingId) {
      console.log('[BOOKINGS-PAGE] Updating selected booking with refreshed data');
      setSelectedBooking(updatedBooking);
    }
  };

  // Check magic link access
  if (magicLinkAccess.isLoading) {
    return <div className={`container max-w-xl ${isMobile ? 'px-2 pb-16' : 'px-4 py-8'} bg-background`}>
        <h1 className="text-3xl font-bold text-center mb-4 text-foreground">Your Bookings</h1>
        <BookingsLoading />
      </div>;
  }
  if (!magicLinkAccess.hasAccess) {
    return <div className={`container max-w-xl ${isMobile ? 'px-2 pb-16' : 'px-4 py-8'} bg-background`}>
        <h1 className="text-3xl font-bold text-center mb-4 text-foreground">Access Denied</h1>
        <div className="text-center p-6 border rounded-lg bg-card">
          <p className="text-muted-foreground mb-2">
            {magicLinkAccess.error || 'You do not have permission to access this booking.'}
          </p>
          <Button onClick={() => window.location.href = '/auth'} className="mt-4">
            Sign In
          </Button>
        </div>
      </div>;
  }
  if (isLoading && bookings.length === 0) {
    return <div className={`container max-w-xl ${isMobile ? 'px-2 pb-16' : 'px-4 py-8'} bg-background`}>
        <h1 className="text-3xl font-bold text-center mb-4 text-foreground">Your Bookings</h1>
        <BookingsLoading />
      </div>;
  }
  if (error) {
    return <div className={`container max-w-xl ${isMobile ? 'px-2 pb-16' : 'px-4 py-8'} bg-background`}>
        <h1 className="text-3xl font-bold text-center mb-4 text-foreground">Your Bookings</h1>
        <BookingsError />
      </div>;
  }
  if (bookings.length === 0 && !isLoading) {
    return <div className={`container max-w-xl ${isMobile ? 'px-2 pb-16' : 'px-4 py-8'} bg-background`}>
        <h1 className="text-3xl font-bold text-center mb-4 text-foreground">Your Bookings</h1>
        
        {/* Filter tabs - always visible */}
        <div className="mb-6">
          <BookingsFilterTabs activeFilter={activeFilter} onFilterChange={filterBookings} />
        </div>
        
        <NoBookings activeFilter={activeFilter} onResetFilter={() => filterBookings("all")} />
      </div>;
  }
  return <div className="container max-w-3xl py-4 md:px-4 md:py-8 w-full bg-background px-[5px]">
      {/* Header section with title, admin badge, button, and filters in a single row */}
      <div className="mb-6 px-[10px]">
        {/* Mobile layout - stacked */}
        <div className="flex flex-col gap-4 md:hidden">
          <div className="flex items-center justify-center gap-2">
            <h1 className="text-3xl font-bold text-foreground">Your Bookings</h1>
            {roles.isAdmin && <Badge className="text-xs px-2 py-0.5 bg-blue-500 hover:bg-blue-600 text-white">
                Admin
              </Badge>}
          </div>
          
          <Link to="/app" className="w-full">
            <Button className="w-full text-white" style={{
            backgroundColor: primaryColor,
            borderColor: primaryColor
          }}>
              Book a Parcel
            </Button>
          </Link>
          
          <BookingsFilterTabs activeFilter={activeFilter} onFilterChange={filterBookings} />
        </div>

        {/* Desktop layout - single row */}
        <div className="hidden md:flex md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-foreground">Bookings</h1>
            {roles.isAdmin && <Badge className="text-xs px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white">
                Admin
              </Badge>}
          </div>
          
          <div className="flex items-center gap-3">
            <div className="min-w-[120px]">
              <BookingsFilterTabs activeFilter={activeFilter} onFilterChange={filterBookings} />
            </div>
            
            <Link to="/app">
              <Button className="text-white whitespace-nowrap h-10" style={{
              backgroundColor: primaryColor,
              borderColor: primaryColor
            }}>
                Book a Parcel
              </Button>
            </Link>
          </div>
        </div>
      </div>
          
      {bookings.length === 0 && !isLoading ? <div className="px-[10px]">
          <NoBookings activeFilter={activeFilter} onResetFilter={() => filterBookings("all")} />
        </div> : <div className="space-y-4 px-[10px]">
          {bookings.map((booking, index) => <BookingCard key={booking.id} booking={booking} onViewDetails={handleOpenDetails} onCancelBooking={setBookingToCancel} ref={index === bookings.length - 1 ? lastBookingElementRef : null} />)}
          
          {/* Loading indicator at the bottom - appears only when fetching more */}
          {isFetchingMore && <LoadingMore />}
          
          {/* End of results indicator - appears only when no more results */}
          {!hasMore && bookings.length > 0 && <EndOfResults />}
          
          {/* Hidden sentinel element for triggering more loads */}
          {hasMore && !isFetchingMore && <div id="booking-list-sentinel" className="h-10 w-full" aria-hidden="true" />}
        </div>}
      
      {/* Booking details modal */}
      <BookingDetailsModal booking={selectedBooking} isOpen={isDetailsModalOpen} onClose={handleCloseDetails} userRoles={roles} onBookingUpdate={handleBookingUpdate} />
      
      {/* Cancel Booking Confirmation Dialog */}
      <AlertDialog open={!!bookingToCancel} onOpenChange={open => !open && setBookingToCancel(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-card-foreground">Cancel Booking</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Are you sure you want to cancel this booking? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-secondary text-secondary-foreground hover:bg-secondary/80">
              No, keep it
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelBooking} className="bg-red-500 hover:bg-red-600 text-white">
              Yes, cancel it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>;
};
export default Bookings;